import React, { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { 
  PencilIcon, 
  TrashIcon, 
  ArrowLeftIcon,
  TagIcon
} from '@heroicons/react/24/outline';
import api from '../../services/api';
import { toast } from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import { useDateFormatter } from '../../hooks/useDateFormatter';
import { stripHtml } from '../../utils/sanitize';

interface Offer {
  id: string;
  name: string;
  offerType: string;
  inactive: boolean;
  slaCategory: string;
  description?: string;
  createdAt: string;
  updatedAt: string;
  User_Offer_createdByIdToUser?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    displayName: string;
  };
  User_Offer_updatedByIdToUser?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    displayName: string;
  };
}

const OfferDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { formatDateTime } = useDateFormatter();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  

  const { data: offer, isLoading, error } = useQuery({
    queryKey: ['offer', id],
    queryFn: async () => {
      const response = await api.get(`/offers/${id}`);
      return response.data as Offer;
    },
    enabled: !!id,
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      await api.delete(`/offers/${id}`);
    },
    onSuccess: () => {
      toast.success('Offer deleted successfully');
      navigate('/offers');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to delete offer');
    },
  });


  const handleDelete = () => {
    deleteMutation.mutate();
    setShowDeleteModal(false);
  };

  const getOfferTypeLabel = (type: string) => {
    return type.replace('_', ' ');
  };

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 text-xl mb-4">Error loading offer</div>
          <Link 
            to="/offers" 
            className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
          >
            Back to Offers
          </Link>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (!offer) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="text-gray-500 text-xl mb-4">Offer not found</div>
          <Link 
            to="/offers" 
            className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
          >
            Back to Offers
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="sm:flex sm:items-center sm:justify-between">
        <div className="sm:flex-auto">
          <div className="flex items-center space-x-3">
            <Link
              to="/offers"
              className="inline-flex items-center text-sm font-medium text-gray-500 hover:text-gray-700"
            >
              <ArrowLeftIcon className="h-5 w-5 mr-1" />
              Back to Offers
            </Link>
          </div>
          <h1 className="mt-2 text-2xl font-semibold text-gray-900 flex items-center">
            <TagIcon className="h-6 w-6 mr-2 text-gray-600" />
            {offer.name}
          </h1>
          <div className="mt-2 flex items-center space-x-4">
            <span
              className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                offer.inactive
                  ? 'bg-red-100 text-red-800'
                  : 'bg-green-100 text-green-800'
              }`}
            >
              {offer.inactive ? 'Inactive' : 'Active'}
            </span>
            <span className="text-sm text-gray-500">
              {getOfferTypeLabel(offer.offerType)}
            </span>
          </div>
        </div>
        {(user?.role === 'admin' || user?.role === 'super_admin') && (
          <div className="mt-4 sm:mt-0 sm:flex sm:space-x-3">
            <Link
              to={`/offers/${offer.id}/edit`}
              className="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
            >
              <PencilIcon className="-ml-1 mr-2 h-5 w-5" />
              Edit
            </Link>
            <button
              onClick={() => setShowDeleteModal(true)}
              className="inline-flex items-center justify-center rounded-md border border-transparent bg-red-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
            >
              <TrashIcon className="-ml-1 mr-2 h-5 w-5" />
              Delete
            </button>
          </div>
        )}
      </div>

      <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Main Details */}
        <div className="lg:col-span-2">
          <div className="bg-white shadow rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Offer Details</h3>
            </div>
            <div className="px-6 py-4 space-y-6">
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Name</label>
                  <p className="mt-1 text-sm text-gray-900">{offer.name}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Offer Type</label>
                  <p className="mt-1 text-sm text-gray-900">{getOfferTypeLabel(offer.offerType)}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">SLA Category</label>
                  <p className="mt-1 text-sm text-gray-900">{offer.slaCategory}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Status</label>
                  <p className="mt-1 text-sm text-gray-900">
                    {offer.inactive ? 'Inactive' : 'Active'}
                  </p>
                </div>
              </div>

              {offer.description && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">Description</label>
                  <p className="mt-1 text-sm text-gray-900 whitespace-pre-wrap">{stripHtml(offer.description)}</p>
                </div>
              )}

            </div>
          </div>

        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Metadata */}
          <div className="bg-white shadow rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Information</h3>
            </div>
            <div className="px-6 py-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Created</label>
                <p className="mt-1 text-sm text-gray-900">{formatDateTime(offer.createdAt)}</p>
                {offer.User_Offer_createdByIdToUser && (
                  <p className="text-xs text-gray-500">
                    by {offer.User_Offer_createdByIdToUser.displayName || `${offer.User_Offer_createdByIdToUser.firstName} ${offer.User_Offer_createdByIdToUser.lastName}`}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Last Updated</label>
                <p className="mt-1 text-sm text-gray-900">{formatDateTime(offer.updatedAt)}</p>
                {offer.User_Offer_updatedByIdToUser && (
                  <p className="text-xs text-gray-500">
                    by {offer.User_Offer_updatedByIdToUser.displayName || `${offer.User_Offer_updatedByIdToUser.firstName} ${offer.User_Offer_updatedByIdToUser.lastName}`}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3 text-center">
              <h3 className="text-lg font-medium text-gray-900">Delete Offer</h3>
              <div className="mt-2 px-7 py-3">
                <p className="text-sm text-gray-500">
                  Are you sure you want to delete this offer? This action cannot be undone.
                </p>
              </div>
              <div className="flex items-center justify-center gap-4 px-4 py-3">
                <button
                  onClick={() => setShowDeleteModal(false)}
                  className="px-4 py-2 bg-gray-300 text-gray-800 text-base font-medium rounded-md shadow-sm hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-300"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  disabled={deleteMutation.isPending}
                  className="px-4 py-2 bg-red-600 text-white text-base font-medium rounded-md shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50"
                >
                  {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OfferDetail;